import { FocusOrigin } from '@angular/cdk/a11y';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild
} from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { map, mapTo, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { translationKeys } from '../../sharedTranslationKeys';
import { TabId } from './tab';
import { TabItemComponent } from './tab-item/tab-item.component';
import { TabHeaderComponent } from './tabs-header/tabs-header.component';

@Component({
  selector: 'avano-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabsComponent implements AfterContentChecked {
  @ViewChild('avanoHeader') tabHeader: TabHeaderComponent;
  sharedTranslationKeys: typeof translationKeys = translationKeys;

  @Input()
  set selectedId(val: TabId) {
    if (this._selectedId !== val) {
      this._selectedId = val;
      this.selectedIdChange.emit(this._selectedId);
      this.changeDet.markForCheck();
    }
  }
  get selectedId(): TabId {
    return this._selectedId;
  }

  // Indicator that async tabs have loaded, if value is true,
  // we trigger header method to recalculate if pagination should be shown.
  // This is very expensive calculation use it as rarely as possible
  @Input()
  set tabsLoaded(val: boolean) {
    if (val !== this._tabsLoaded) {
      this._tabsLoaded = val;
      if (val && this.tabHeader) {
        this.tabHeader.updatePagination();
        this.tabHeader.scrollToCurrentLabel();
        this.changeDet.markForCheck();
      }
    }
  }
  get tabsLoaded(): boolean {
    return this._tabsLoaded;
  }

  @Output() readonly selectedIdChange: EventEmitter<TabId> = new EventEmitter();

  private _tabsQueryListSet$ = new ReplaySubject<QueryList<TabItemComponent>>();
  private _tabs: QueryList<TabItemComponent> = undefined;
  @ContentChildren(TabItemComponent) set tabs(tabsQuery: QueryList<TabItemComponent>) {
    this._tabs = tabsQuery;
    this._tabsQueryListSet$.next(tabsQuery);
  }
  get tabs(): QueryList<TabItemComponent> {
    return this._tabs;
  }

  tabItems$: Observable<TabItemComponent[]> = this._tabsQueryListSet$.pipe(
    switchMap((query) => query.changes.pipe(startWith({}), mapTo(query))),
    map((queryList: QueryList<TabItemComponent>) => (queryList ? queryList.toArray() : [])),
    shareReplay()
  );
  fixedItems$: Observable<TabItemComponent[]> = this.tabItems$.pipe(
    map((tabs) => tabs.filter(({ tabId }: TabItemComponent) => tabId === 'FIXED' || tabId === 'DEFAULT'))
  );
  additionalItems$: Observable<TabItemComponent[]> = this.tabItems$.pipe(
    map((tabs) => tabs.filter(({ tabId }: TabItemComponent) => tabId !== 'DEFAULT' && tabId !== 'FIXED'))
  );

  private _selectedId: TabId;
  private _tabsLoaded: boolean;

  constructor(private changeDet: ChangeDetectorRef) {}

  ngAfterContentChecked(): void {
    if (this.selectedId === undefined) {
      Promise.resolve().then(() => {
        const stillUndefined = this.selectedId === undefined; // need to check again since value may have been set in meantime
        if (stillUndefined) this.selectFirstIdentifiableTab();
      });
    }
  }

  closeTab(id: TabId, items: TabItemComponent[]): void {
    const found = items.find(({ tabId }: TabItemComponent) => id === tabId);
    found.closeFromMenu();
  }

  /** Callback for when the focused state of a tab has changed. */
  tabFocusChanged(focusOrigin: FocusOrigin, id: TabId): void {
    // Mouse/touch focus happens during the `mousedown`/`touchstart` phase which
    // can cause the tab to be moved out from under the pointer, interrupting the
    // click sequence . We don't need to scroll the tab into view for
    // such cases anyway, because it will be done when the tab becomes selected.
    if (focusOrigin && focusOrigin !== 'mouse' && focusOrigin !== 'touch') {
      this.tabHeader.focusId = id;
    }
  }

  /** Retrieves the tabindex for the tab. */
  getTabIndex(id: TabId): number | null {
    return this.selectedId === id ? 0 : -1;
  }

  private selectFirstIdentifiableTab() {
    const fallbackTab: TabItemComponent | undefined = this.tabs.find((tab) => tab.tabId !== undefined);
    if (fallbackTab) {
      this.selectedId = fallbackTab.tabId;
      this.changeDet.markForCheck();
    }
  }
}
